import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Navigation, FilePlus2, Receipt } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth, usePrefs } from "@/lib/context";
import { fetcher, useCategories } from "@/lib/hooks";
import { ItemsEditor, cleanItems, CurrencySelect } from "@/components/FormBits";
import { Field } from "@/components/PropertyDialog";
import { DateInput } from "@/components/DateInput";
import { QuoteRow } from "@/pages/Finance";
import { LiveMap, isLive, toMapItem, EtaLine } from "@/components/LiveMap";
import { ShareTrackingLink } from "@/components/ShareTrackingLink";
import { ReviewCard } from "@/components/Reviews";
import { useGpsShare } from "@/components/GpsShare";

function DocDialog({ tk, mode, quote }) {
  const { t } = usePrefs();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const init = quote ? quote.items : [{ label: "", qty: 1, unit_price: "" }];
  const [f, setF] = useState({ items: init, currency: quote?.currency || "EUR", note: "", due_date: "" });
  const save = async () => {
    try {
      const items = cleanItems(f.items);
      if (mode === "quote") await api.post("/quotes", { ticket_id: tk.id, items, currency: f.currency, note: f.note });
      else await api.post("/invoices", { ticket_id: tk.id, quote_id: quote?.id || null, items, currency: f.currency, note: f.note, due_date: f.due_date });
      qc.invalidateQueries();
      setOpen(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const isQ = mode === "quote";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full rounded-sm" data-testid={isQ ? "submit-quote-button" : "issue-invoice-button"}>
          {isQ ? <FilePlus2 size={14} className="mr-1" /> : <Receipt size={14} className="mr-1" />}{t(isQ ? "submitQuote" : "issueInvoice")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl rounded-sm" data-testid={isQ ? "quote-dialog" : "provider-invoice-dialog"}>
        <DialogHeader><DialogTitle className="font-display text-2xl">{t(isQ ? "submitQuote" : "issueInvoice")} — <span className="font-data text-base">{tk.number}</span></DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("currency")}><CurrencySelect value={f.currency} onChange={(v) => setF({ ...f, currency: v })} testId="doc-currency-select" /></Field>
          {!isQ && <Field label={t("due")}><DateInput value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="doc-due-input" /></Field>}
        </div>
        <ItemsEditor value={f.items} onChange={(items) => setF({ ...f, items })} currency={f.currency} />
        <Field label={t("notes")}><Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="doc-note-input" /></Field>
        <Button onClick={save} disabled={!cleanItems(f.items).length || (!isQ && !f.due_date)} className="rounded-sm bg-[#0F172A]" data-testid="doc-save-button">{t("save")}</Button>
      </DialogContent>
    </Dialog>
  );
}

export function TicketQuotes({ tk }) {
  const { user } = useAuth();
  const { t } = usePrefs();
  const { data: quotes = [] } = useQuery({ queryKey: ["quotes", tk.id], queryFn: () => fetcher(`/quotes?ticket_id=${tk.id}`), enabled: user.role !== "locataire" });
  const { data: invoices = [] } = useQuery({ queryKey: ["invoices"], queryFn: () => fetcher("/invoices"), enabled: user.role === "prestataire" });
  if (user.role === "locataire") return null;
  const mine = user.role === "prestataire" && tk.assigned_to === user.id;
  const invoiced = new Set(invoices.filter((i) => i.status !== "annulee").map((i) => i.quote_id));
  const approved = quotes.find((q) => q.status === "approuve" && !invoiced.has(q.id));
  if (!quotes.length && !mine) return null;
  return (
    <div className="rounded-sm border border-slate-200 bg-white" data-testid="ticket-quotes">
      <div className="border-b px-5 py-3 font-display text-lg font-bold">{t("quotes")}</div>
      <div className="divide-y divide-slate-100">{quotes.map((q) => <QuoteRow key={q.id} q={q} showTicket={false} />)}</div>
      {mine && <div className="space-y-2 p-4"><DocDialog tk={tk} mode="quote" />{["en_cours", "resolu", "cloture"].includes(tk.status) || quotes.some((q) => q.status === "approuve") ? <DocDialog key={approved?.id || "none"} tk={tk} mode="invoice" quote={approved} /> : null}</div>}
    </div>
  );
}

function GpsStats({ loc }) {
  const { t, fdatetime } = usePrefs();
  return (
    <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]" data-testid="gps-stats">
      <div><div className="text-slate-500">{t("lastUpdate")}</div><div className="font-semibold" data-testid="gps-last-update">{fdatetime(loc.at)}</div></div>
      <div><div className="text-slate-500">{t("speed")}</div><div className="font-data font-semibold" data-testid="gps-speed">{loc.speed != null ? `${Math.round(loc.speed * 3.6)} km/h` : "—"}</div></div>
      <div><div className="text-slate-500">{t("accuracy")}</div><div className="font-data font-semibold" data-testid="gps-accuracy">{loc.accuracy != null ? `± ${Math.round(loc.accuracy)} m` : "—"}</div></div>
    </div>
  );
}

export function ConciergePanel({ tk }) {
  const { user } = useAuth();
  const { t, fdatetime } = usePrefs();
  const { label } = useCategories();
  const gps = useGpsShare();
  const on = gps.ticket?.id === tk.id;
  const setOn = (v) => (v ? gps.start(tk) : gps.stop());
  const done = ["resolu", "cloture"].includes(tk.status);
  useEffect(() => { if (on && done) gps.stop(); }, [on, done]); // eslint-disable-line react-hooks/exhaustive-deps
  const loc = tk.location;
  const canShare = user.role === "prestataire" && tk.assigned_to === user.id && !["resolu", "cloture"].includes(tk.status);
  return (
    <div className="space-y-3 rounded-sm border border-[#e6d39f] bg-[#fdf8ec] p-5 text-sm" data-testid="concierge-panel">
      <div className="eyebrow">{t("concierge")} · {label(tk.service)}</div>
      <div className="flex justify-between"><span className="text-slate-500">{t("scheduledAt")}</span><span>{fdatetime(tk.scheduled_at)}</span></div>
      {tk.service === "transport" && <>
        <div className="flex justify-between gap-2"><span className="text-slate-500">{t("pickup")}</span><span className="text-end">{tk.pickup || "—"}</span></div>
        <div className="flex justify-between gap-2"><span className="text-slate-500">{t("destination")}</span><span className="text-end">{tk.destination || "—"}</span></div>
        <div className="pt-2">
          <div className="mb-2 flex items-center justify-between gap-1.5 font-semibold">
            <span className="flex items-center gap-1.5"><Navigation size={14} />{t("gps")}</span>
            {loc && isLive(loc) && <span className="flex items-center gap-1 text-[11px] font-bold uppercase text-emerald-700" data-testid="gps-live-badge"><span className="live-dot" />{t("enRoute")}</span>}
          </div>
          {(loc || tk.pickup_geo || tk.destination_geo) && <LiveMap testId="gps-map" follow height="h-56" transports={[toMapItem(tk, tk.assigned_name || tk.number)]} />}
          {loc ? <><GpsStats loc={loc} /><div className="mt-2"><EtaLine eta={tk.eta} /></div></> : <p className="text-xs text-slate-500" data-testid="gps-no-location">{t("noLocation")}</p>}
          {canShare && <Button size="sm" onClick={() => setOn(!on)} className={`mt-3 w-full rounded-sm ${on ? "bg-rose-700 hover:bg-rose-800" : "bg-[#0F172A] hover:bg-[#B8860B]"}`} data-testid="gps-share-button">{on ? t("stopSharing") : t("shareLocation")}</Button>}
          {on && <p className="mt-2 flex items-center gap-1.5 text-[11px] text-emerald-700" data-testid="gps-sharing-indicator"><span className="live-dot" />{t("sharingActive")}</p>}
          {!done && (["admin", "supervision", "gestionnaire"].includes(user.role) || tk.created_by === user.id) && <ShareTrackingLink tk={tk} />}
          {tk.review && <div className="mt-3 rounded-sm border border-[#e6d39f] bg-white" data-testid="ticket-review"><div className="px-5 pt-3 text-[10px] font-bold uppercase tracking-[0.14em] text-[#996515]">{t("reviews")}</div><ReviewCard r={tk.review} showTicket={false} /></div>}
        </div>
      </>}
    </div>
  );
}
